import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const RENEW_FILTERS = [
  { key: "search", type: "search", label: "Search holder name or credential number", paths: ["holder_name", "credential_number"] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "submitted", label: "Submitted" }, { value: "under_review", label: "Under review" },
      { value: "approved", label: "Approved" }, { value: "denied", label: "Denied" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Submitted from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Submitted to", path: "created_at" },
];

export default function Renewals() {
  const { user } = useAuth();
  const [renewals, setRenewals] = useState([]);
  const [creds, setCreds] = useState([]);
  const [renewable, setRenewable] = useState(null);
  const [credId, setCredId] = useState("");
  const [years, setYears] = useState(2);
  const [hours, setHours] = useState("");
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const isPract = user.role === "practitioner" || user.role === "applicant";
    const reqs = [api.get("/renewals"), api.get("/credentials")];
    if (isPract) reqs.push(api.get("/credentials/renewable"));
    const [r, c, rn] = await Promise.all(reqs);
    setRenewals(r.data);
    setCreds(c.data);
    if (rn) {
      setRenewable(rn.data);
      // Auto-select highest renewable credential when it lives in db.credentials.
      if (rn.data?.type === "credential" && rn.data?.id) {
        setCredId(rn.data.id);
      }
    }
  };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!credId || !attest) { toast.error("Select credential and attest"); return; }
    setBusy(true);
    try {
      await api.post("/renewals", {
        credential_id: credId,
        period_years: Number(years),
        ce_hours_claimed: hours ? Number(hours) : null,
        attestation: attest,
      });
      toast.success("Renewal submitted");
      setCredId(""); setHours(""); setAttest(false);
      reload();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/renewals/${id}/approve`);
      toast.success("Renewal approved & credential extended");
      reload();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const isPractitioner = user.role === "practitioner" || user.role === "applicant";

  const filters = useFilters(renewals, RENEW_FILTERS, { defaultSort: { field: "created_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="renewals-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">IBTHM · Credential lifecycle</div>
        <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><RefreshCw className="h-7 w-7" />Renewals</h1>
        <p className="text-sm text-slate-500 mt-1">Submit and review credential renewals.</p>
      </div>

      {isPractitioner && renewable && (
        <Card
          className={`p-4 mb-4 border ${
            renewable.id
              ? "border-emerald-200 bg-emerald-50"
              : "border-amber-200 bg-amber-50"
          }`}
          data-testid="renewable-banner"
        >
          <div className="flex items-start gap-3">
            {renewable.id ? (
              <ShieldCheck className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-600 mb-1">
                Renewal eligibility
              </div>
              {renewable.id ? (
                <>
                  <div className="text-sm text-slate-800">
                    Highest active credential:{" "}
                    <span className="font-semibold">{renewable.label}</span>{" "}
                    <span className="font-mono-data text-xs text-slate-600">({renewable.number})</span>
                  </div>
                  {renewable.type === "license" && (
                    <div className="text-xs text-slate-600 mt-1">
                      LHT renewal is handled in the License module. Lower credentials inherit renewal automatically.
                    </div>
                  )}
                  {renewable.all_active?.length > 1 && (
                    <div className="text-[11px] text-slate-500 mt-2">
                      Held credentials:{" "}
                      {renewable.all_active.map((c) => (
                        <span key={c.id} className="mr-2 font-mono-data">{c.label}/{c.number}</span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-800">No active credentials found to renew.</div>
              )}
            </div>
          </div>
        </Card>
      )}

      {isPractitioner && creds.length > 0 && (
        <Card className="p-5 border-slate-200 bg-slate-50 mb-5">
          <h2 className="font-heading text-sm font-semibold text-primary mb-3">Submit a renewal</h2>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <Label className="mb-1.5 block text-xs">Credential</Label>
              <Select value={credId} onValueChange={setCredId}>
                <SelectTrigger data-testid="renew-cred-select"><SelectValue placeholder="Choose credential" /></SelectTrigger>
                <SelectContent>
                  {creds.map((c) => <SelectItem key={c.id} value={c.id}>{c.credential_number} · {c.credential_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="mb-1.5 block text-xs">Period (years)</Label>
              <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} data-testid="renew-years" />
            </div>
            <div className="md:col-span-3">
              <Label className="mb-1.5 block text-xs">CE hours claimed</Label>
              <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="renew-hours" />
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button onClick={submit} disabled={busy} className="w-full bg-primary text-white hover:bg-[#113A64]" data-testid="renew-submit">Submit</Button>
            </div>
          </div>
          <label className="flex items-start gap-2 mt-3 text-xs text-slate-700">
            <Checkbox checked={attest} onCheckedChange={setAttest} data-testid="renew-attest" className="mt-0.5" />
            I attest that the CE hours and continued competency claims are true.
          </label>
        </Card>
      )}

      <FilterPanel
        schemas={RENEW_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={renewals.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Submitted date" },
          { key: "holder_name", label: "Holder name" },
          { key: "status", label: "Status" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="renewals-filters"
      />

      {isAdmin && <BulkActionsBar collection="renewals" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-4 w-10">
                    <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="renewals-select-all" />
                  </th>
                )}
                <th className="p-4">Credential</th>
                <th className="p-4">Holder</th>
                <th className="p-4">Period</th>
                <th className="p-4">CE hours</th>
                <th className="p-4">Status</th>
                <th className="p-4">Submitted</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filters.filtered.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="p-10 text-center text-slate-500">No renewals match the current filters</td></tr>}
              {filters.filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  {isAdmin && (
                    <td className="p-4">
                      <Checkbox checked={selection.isSelected(r.id)} onCheckedChange={() => selection.toggle(r.id)} data-testid={`renewal-select-${r.id}`} />
                    </td>
                  )}
                  <td className="p-4 font-mono-data text-xs text-primary">{r.credential_number}</td>
                  <td className="p-4 text-sm text-primary">{r.holder_name}</td>
                  <td className="p-4 text-xs">{r.period_years} yr</td>
                  <td className="p-4 text-xs">{r.ce_hours_claimed ?? "—"}</td>
                  <td className="p-4"><Badge variant="outline" className="capitalize">{r.status}</Badge></td>
                  <td className="p-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    {user.role === "admin" && r.status === "submitted" && (
                      <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => approve(r.id)} data-testid={`renew-approve-${r.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
